import React, { useState } from 'react'
import { router, usePage, Link, Head } from '@inertiajs/react'
import { Loader2, Mail, AlertCircle, ArrowLeft, Send } from 'lucide-react'
import Marketing3AuthShell from '@/components/marketing3/Marketing3AuthShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FALLBACK_BRAND } from '@/lib/brand'
import '../marketing/marketing3.css'
import { csrfToken } from '@/lib/csrf'


// mkt3 teal focus ring — see SignIn.tsx.
const FIELD_FOCUS = 'border-stone-200 focus-visible:ring-teal-600 focus-visible:ring-offset-0'

interface ForgotPasswordProps {
  brand?: string | null
}

// Users::PasswordsController inherits ApplicationController (not Operator::BaseController), so a
// redirect's flash surfaces on Inertia's shared `flash` prop — read here, display only.
export default function ForgotPassword({ brand: brandProp }: ForgotPasswordProps) {
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND

  const { props } = usePage<{ flash?: { alert?: string | null; notice?: string | null } }>()
  const flashAlert = props.flash?.alert

  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const canSubmit = email.trim().length > 0 && !submitting

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    // State-changing — router.post (the documented exception to the <Link> nav rule). Devise's
    // recoverable expects params nested under `user`. On success the controller redirects to
    // sign-in with a generic notice (no account enumeration); the SignIn page shows it.
    router.post(
      '/users/password',
      { user: { email } },
      {
        headers: { 'X-CSRF-Token': csrfToken() },
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <Marketing3AuthShell brand={brand}>
      <Head title="Forgot password" />
      {/* Lead-in */}
      <div className="text-center">
        <h1 className="mkt3-display mkt3-h3">Forgot your password?</h1>
        <p className="mkt3-prose mx-auto mt-2 text-sm">
          Enter your email and we&apos;ll send you a link to choose a new one.
        </p>
      </div>

      {/* Error (rare — the happy path redirects to sign-in with a notice). */}
      {flashAlert && (
        <div className="mt-6 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{flashAlert}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-7 space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email" className="mkt3-label normal-case tracking-[0.12em] text-slate-500">
            Email
          </Label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              placeholder="you@company.com"
              className={`h-11 rounded-lg bg-white pl-9 ${FIELD_FOCUS}`}
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={!canSubmit}
          className="mkt3-cta h-11 w-full rounded-lg bg-teal-600 text-sm font-semibold text-white hover:bg-teal-700"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Send reset link
            </>
          )}
        </Button>
      </form>

      {/* Back to sign-in — /users/sign_in IS an Inertia page, so <Link> is correct here. */}
      <p className="mt-7 text-center text-sm">
        <Link
          href="/users/sign_in"
          className="inline-flex items-center gap-1.5 font-medium text-teal-700 hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to sign in
        </Link>
      </p>
    </Marketing3AuthShell>
  )
}
